/**
 * P3.7 follow-through (2026-09-05) — numeric cross-check of the SED cooling
 * model's sink-class withdrawal coefficient (`L/MWh-thermal`, ±40% class)
 * against the deterministic WATER-USE SCREEN, on the ONE volume both quantify:
 * full-load wet-tower makeup WITHDRAWAL.
 *
 * Until now the coefficient line was labelled and explicitly never converted
 * into screen bands (P3.7). That is still true: this module prints NO gal/day
 * figure of its own, only a ratio and words, so WATER-TRUTH-1 sees no new
 * quantity and the screen band stays the only quotable project water figure.
 *
 * Volumes: the screen's `wet-cooling-tower-withdrawal` sensitivity is a
 * WITHDRAWAL band (makeup = evaporation + blowdown + drift) on a full-load
 * basis (`fullLoadGallonsPerDay`); the coefficient is withdrawal per MWh of
 * REJECTED HEAT. Multiplying the coefficient by the screen's own facility
 * heat (MW thermal, the IT load plus cooling overhead the screen resolved)
 * for 24 h gives an implied full-load withdrawal on the same basis.
 *
 * Stated assumptions (rendered with the result, never silent):
 *   1. The screen's facility heat is rejected ENTIRELY through the declared
 *      cold-end sink (no free-cooling hours, no waste-heat offtake).
 *   2. Full-load basis on both sides: no annual utilization applied.
 *   3. The coefficient is a sink-class planning estimate (±40%); the band is a
 *      research-backed screening estimate. Agreement means order-of-magnitude
 *      consistency, not validation of either.
 *
 * Other sinks are NOT compared: once-through (screen does not quantify it),
 * absorption-CCHP and hybrid (no screen scenario on that basis), dry and
 * free-cooling (coefficient 0; the screen's dry scenario is zero makeup with
 * an optional adiabatic-assist scenario the coefficient does not carry).
 */
import type { CoolingEvidence } from '../../types/siteEvidence';
import type { WaterUseScreeningResult } from '../../types/economicContribution';

export const LITRES_PER_US_GALLON = 3.785411784;
/** `crossValidation.checks` rule id carried by `runCrossValidation`. */
export const SINK_WITHDRAWAL_CROSS_CHECK_RULE = 'cooling-withdrawal-vs-screen';
/** The ±40% value-level band `siteEvidence` attaches to the sink-class coefficient. */
export const SINK_COEFFICIENT_CLASS_TOLERANCE = 0.4;

export type SinkWithdrawalCrossCheckOutcome =
  | 'consistent'
  | 'divergent'
  | 'sink-mismatch'
  | 'not-comparable';

export interface SinkWithdrawalCrossCheck {
  outcome: SinkWithdrawalCrossCheckOutcome;
  /** Implied full-load withdrawal ÷ screen band midpoint; null unless compared. */
  ratio: number | null;
  /** Reader-facing sentence. Carries the ratio and words, never a gal/day figure. */
  sentence: string;
  assumptions: readonly string[];
  /** SED fields involved (cross-validation `fields`). */
  fields: readonly string[];
}

export const SINK_WITHDRAWAL_CROSS_CHECK_ASSUMPTIONS: readonly string[] = [
  "the screen's facility heat is rejected entirely through the declared cold-end sink",
  'full-load basis on both sides, no annual utilization applied',
  'coefficient is a sink-class estimate (±40% class); the screen band is a research-backed screening estimate',
];

const FIELDS: readonly string[] = [
  'cooling.waterWithdrawal.litersPerMwhThermal',
  'cooling.bopHeatRejection',
  'economicEvidence.waterUseScreening.resolvedContext.heatRejection',
  'economicEvidence.waterUseScreening.scenarios',
];

const fmtRatio = (ratio: number): string =>
  ratio.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function result(
  outcome: SinkWithdrawalCrossCheckOutcome,
  ratio: number | null,
  sentence: string,
): SinkWithdrawalCrossCheck {
  return { outcome, ratio, sentence, assumptions: SINK_WITHDRAWAL_CROSS_CHECK_ASSUMPTIONS, fields: FIELDS };
}

/**
 * Null when there is nothing to cross-check (no coefficient, no calculated
 * screen, or no resolved context) — silence is correct there; a sentence
 * about an absent comparison would itself be a claim.
 */
export function crossCheckSinkWithdrawalAgainstScreen(
  cooling: Pick<CoolingEvidence, 'waterWithdrawal'> | null | undefined,
  screen: WaterUseScreeningResult | null | undefined,
): SinkWithdrawalCrossCheck | null {
  const withdrawal = cooling?.waterWithdrawal;
  if (!withdrawal || !screen || screen.status !== 'calculated' || !screen.resolvedContext) return null;
  const sink = withdrawal.sink;
  const screenSink = screen.resolvedContext.heatRejection;

  if (screenSink !== sink) {
    return result(
      'sink-mismatch',
      null,
      `Withdrawal cross-check: the WATER-USE SCREEN was resolved for a "${screenSink}" cold end while the SED cooling model declares "${sink}"; `
      + 'the two describe different designs, so neither figure may be quoted as the other\'s basis — reconcile the Project Intent before either is cited.',
    );
  }

  if (sink === 'once-through') {
    return result(
      'not-comparable',
      null,
      'Withdrawal cross-check: not comparable — once-through withdrawal is mostly returned, not consumed, and the WATER-USE SCREEN does not quantify a once-through sink.',
    );
  }
  if (sink === 'absorption-cchp' || sink === 'hybrid') {
    return result(
      'not-comparable',
      null,
      `Withdrawal cross-check: not comparable — the WATER-USE SCREEN carries no scenario on a "${sink}" basis, so the sink-class coefficient has nothing of the same volume to agree with.`,
    );
  }

  const coefficient = withdrawal.litersPerMwhThermal;
  if (sink === 'dry-cooler' || sink === 'free-cooling') {
    const dry = screen.scenarios.find((scenario) => scenario.kind === 'dry-cooling');
    const band = dry?.fullLoadGallonsPerDay ?? dry?.averageGallonsPerDay ?? null;
    if (!dry || !band) {
      return result('not-comparable', null,
        'Withdrawal cross-check: not comparable — the WATER-USE SCREEN carries no dry-cooling scenario to set beside the zero-makeup coefficient.');
    }
    if ((coefficient ?? 0) === 0 && band.high === 0) {
      return result('consistent', null,
        'Withdrawal cross-check: consistent — the sink-class coefficient and the WATER-USE SCREEN\'s dry-cooling scenario both read zero makeup withdrawal'
        + (dry.adiabaticWaterScreening ? '; the screen\'s adiabatic-assist water is a separate scenario the coefficient does not carry.' : '.'));
    }
    return result('divergent', null,
      'Withdrawal cross-check: divergent — the sink-class coefficient reads zero makeup withdrawal while the WATER-USE SCREEN\'s dry-cooling scenario carries a non-zero band; '
      + 'quote neither as the other\'s basis until the cooling design is reconciled.');
  }

  // wet-cooling-tower — the one same-volume comparison.
  const wet = screen.scenarios.find((scenario) => scenario.kind === 'wet-cooling-tower-withdrawal');
  const band = wet?.fullLoadGallonsPerDay ?? null;
  if (coefficient == null || !Number.isFinite(coefficient) || coefficient <= 0 || !wet || !band || band.high <= 0) {
    return result('not-comparable', null,
      'Withdrawal cross-check: not comparable — the WATER-USE SCREEN carries no full-load wet-tower withdrawal sensitivity to set beside the sink-class coefficient.');
  }
  const heat = screen.resolvedContext.facilityHeatMw;
  if (!heat || !(heat.high > 0)) {
    return result('not-comparable', null,
      'Withdrawal cross-check: not comparable — the WATER-USE SCREEN resolved no facility heat to apply the sink-class coefficient to.');
  }
  const impliedLow = (coefficient * heat.low * 24) / LITRES_PER_US_GALLON;
  const impliedHigh = (coefficient * heat.high * 24) / LITRES_PER_US_GALLON;
  const bandMid = (band.low + band.high) / 2;
  const impliedMid = (impliedLow + impliedHigh) / 2;
  const ratio = bandMid > 0 ? impliedMid / bandMid : null;
  const tolerance = SINK_COEFFICIENT_CLASS_TOLERANCE;
  const overlaps = impliedHigh * (1 + tolerance) >= band.low && impliedLow * (1 - tolerance) <= band.high;
  if (ratio == null) {
    return result('not-comparable', null,
      'Withdrawal cross-check: not comparable — the WATER-USE SCREEN\'s wet-tower withdrawal band has no midpoint to compare against.');
  }
  if (overlaps) {
    return result('consistent', ratio,
      `Withdrawal cross-check: consistent — the sink-class coefficient applied to the screen's own facility heat implies a full-load makeup withdrawal about ${fmtRatio(ratio)}× the WATER-USE SCREEN's wet-tower withdrawal band midpoint, `
      + 'inside the coefficient\'s ±40% class; the two volumes agree at screening precision and the screen band remains the only quotable figure.');
  }
  return result('divergent', ratio,
    `Withdrawal cross-check: divergent — the sink-class coefficient applied to the screen's own facility heat implies a full-load makeup withdrawal about ${fmtRatio(ratio)}× the WATER-USE SCREEN's wet-tower withdrawal band midpoint, `
    + 'outside the coefficient\'s ±40% class; the coefficient family or the screen\'s cooling assumptions disagree — quote neither as the other\'s basis and keep the screen band as the only quotable figure until the cooling design is reconciled.');
}
